<template>
  <component
    :is="tag"
    :class="classes"
    :disabled="disabled || loading"
    @click="onClick"
  >
    <slot :download="download" :loading="loading">
      {{ loading ? loadingLabel : label }}
    </slot>
  </component>
</template>

<script>
import Vue from 'vue'
import axios from 'axios'
import store from '@/store'

export default {
  name: 'DownloadInvoice',
  props: {
    orderId: { type: [String, Number], required: true },
    tag: { type: String, default: 'button' },
    label: { type: String, default: 'Download Invoice' },
    loadingLabel: { type: String, default: 'Downloading…' },
    classes: { type: [String, Array, Object], default: () => ['full-width'] },
    disabled: { type: Boolean, default: false },
    filenameBase: { type: String, default: 'Invoice' },
    withCredentials: { type: Boolean, default: false }
  },
  data () {
    return { loading: false }
  },
  methods: {
    onClick () {
      if (!this.loading && !this.disabled) this.download()
    },

    async download () {
      this.loading = true
      this.$emit('start')
      try {
        const url = this.buildUrl()

        let wrapperHeaders = {}
        try {
          if (Vue.api && typeof Vue.api.headers === 'function') {
            wrapperHeaders = Vue.api.headers()
          } else {
            wrapperHeaders = { Authorization: `Stylist ${store.getters.getToken}` }
          }
        } catch (e) {
          wrapperHeaders = { Authorization: `Stylist ${store.getters.getToken}` }
        }

        const res = await axios.get(url, {
          responseType: 'arraybuffer',
          headers: {
            Accept: 'application/pdf, application/octet-stream, */*',
            ...wrapperHeaders
          },
          withCredentials: this.withCredentials,
          validateStatus: () => true
        })

        if (res.status < 200 || res.status >= 300) {
          const text = new TextDecoder().decode(new Uint8Array(res.data || []))
          let msg = `HTTP ${res.status}`
          try {
            const json = JSON.parse(text)
            msg += `: ${json.message || JSON.stringify(json).slice(0, 300)}`
          } catch (_) {
            if (text) msg += `\n${text.slice(0, 300)}`
          }
          throw new Error(msg)
        }

        const ct = (res.headers['content-type'] || '').toLowerCase()
        const buf = new Uint8Array(res.data)
        const header = new TextDecoder().decode(buf.slice(0, 5))

        // Sanity check: PDF signature or content-type
        if (!ct.includes('pdf') && header !== '%PDF-') {
          const preview = new TextDecoder().decode(buf.slice(0, 300))
          throw new Error('Unexpected content (not a PDF). Preview:\n' + preview)
        }

        const blob = new Blob([res.data], { type: 'application/pdf' })
        const filename = this.filenameFromHeaders(res.headers) || `${this.filenameBase}-${this.orderId}.pdf`
        this.saveBlob(blob, filename)
        this.$emit('success', { filename, headers: res.headers })
      } catch (err) {
        if (Vue.$ui && typeof Vue.$ui.error === 'function') {
          Vue.$ui.error('Could not download invoice: ' + (err && err.message ? err.message : 'Unknown error'))
        } else {
          alert('Could not download invoice')
        }
        this.$emit('error', err)
      } finally {
        this.loading = false
      }
    },

    buildUrl () {
      if (this.endpoint) return this.wrapApiUrl(this.endpoint)
      const path = `/invoice/${this.orderId}/download`
      return this.wrapApiUrl(path)
    },

    wrapApiUrl (pathOrUrl) {
      try {
        if (Vue.api && typeof Vue.api.url === 'function') return Vue.api.url(pathOrUrl)
      } catch (e) {}
      return pathOrUrl
    },

    filenameFromHeaders (headers) {
      const cd = headers['content-disposition'] || ''
      const m = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(cd)
      if (!m) return null
      try { return decodeURIComponent(m[1] || m[2]) } catch (_) { return m[1] || m[2] }
    },

    saveBlob (blob, filename) {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    }
  }
}
</script>
